import api from './api.ts';

export type InvestmentCategory =
  | 'crypto'
  | 'offshore'
  | 'stocks'
  | 'fixed_income'
  | 'funds'
  | 'other';

export type InvestmentStatus = 'active' | 'sold';

export interface Investment {
  id: string;
  description: string;
  category: InvestmentCategory;
  investedValue: number;
  currentValue: number;
  yieldAmount: number;
  yieldPercent: number;
  status: InvestmentStatus;
  currency?: string;
  ticker?: string | null;
  quantity?: number | null;
  unitPrice?: number | null;
  ultimaAtualizacao?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface InvestmentPayload {
  description: string;
  category: InvestmentCategory;
  investedValue: number;
  currentValue?: number;
  status?: InvestmentStatus;
  currency?: string;
  ticker?: string | null;
  quantity?: number | null;
  unitPrice?: number | null;
}

export type ImportSource = 'b3_posicao' | 'b3_movimentacao' | 'xp' | 'clear' | 'btg';

export type ImportPreviewAction = 'novo' | 'atualizar' | 'ignorar';

export interface ImportPreviewRow {
  rowKey: string;
  ticker: string;
  description: string;
  category: string;
  quantity: number | null;
  unitPrice: number | null;
  valor: number;
  action: ImportPreviewAction;
  matchedInvestmentId: string | null;
  include: boolean;
}

export interface ImportPreviewResult {
  source: ImportSource;
  sourceLabel: string;
  filename: string;
  referenceDate: string;
  ultimaAtualizacaoPreview: string;
  rows: ImportPreviewRow[];
  summary: {
    novos: number;
    atualizar: number;
    ignorados: number;
    valorTotal: number;
    historyPoints: number;
  };
}

export interface ImportCommitResult {
  source: ImportSource;
  sourceLabel: string;
  filename: string;
  ultimaAtualizacao: string;
  ultimaAtualizacaoLabel: string;
  created: number;
  updated: number;
  ignored: number;
  historyAppended: number;
  valorImportado: number;
  portfolioUltimaAtualizacao: string;
}

export interface PortfolioMeta {
  ultimaAtualizacao: string | null;
  ultimaAtualizacaoLabel: string | null;
}

export interface PortfolioCategorySlice {
  key: string;
  label: string;
  color: string;
  value: number;
  percent: number;
}

export interface PortfolioHistoryPoint {
  month: string;
  label: string;
  /** Índice 100 desde o 1º ponto com valor (filtro atual) */
  portfolio: number;
  /** Valor absoluto R$ (opcional, para tooltips) */
  portfolioValue?: number;
  /** Índice 100 SELIC desde o 1º ponto */
  selic: number | null;
  /** Taxa SELIC realizada no mês, em % a.m. (ex.: 1.04 = 1,04% a.m.). Null se a série não veio. */
  selicMonthRatePct?: number | null;
  /** Retorno do mês da carteira − SELIC, em pontos percentuais (ex.: -2.1 → −2,1%) */
  vsSelicPct?: number | null;
}

export interface PortfolioCharts {
  total: number;
  invested: number;
  byCategory: PortfolioCategorySlice[];
  history: PortfolioHistoryPoint[];
  appliedFilters?: {
    status: string;
    category: string;
    search: string;
    positions: number;
  };
  selic: {
    source: string;
    sourceLabel: string;
    label: string;
    /** Current annual SELIC % from BrasilAPI TAXAS (spot only) */
    currentRateAa?: number | null;
    currentRateSource?: string | null;
    currentRateLabel?: string | null;
  };
  kpis: {
    positions: number;
    positionsHint: string;
    patrimonio: number;
    rendimentoMes: number;
    rendimentoMesPct: number;
    vencendo90d: number;
  };
  empty: boolean;
}

export interface PriceHistoryPoint {
  id: string;
  investmentId: string;
  timestamp: string;
  preco: number | null;
  valor: number;
  fonte: string | null;
}

const basePath = '/investments';

export const InvestmentsAPI = {
  async list(): Promise<Investment[]> {
    const { data } = await api.get(basePath);
    return data;
  },

  async get(id: string): Promise<Investment> {
    const { data } = await api.get(`${basePath}/${id}`);
    return data;
  },

  async create(payload: InvestmentPayload): Promise<Investment> {
    const { data } = await api.post(basePath, payload);
    return data;
  },

  async update(id: string, payload: InvestmentPayload): Promise<Investment> {
    const { data } = await api.put(`${basePath}/${id}`, payload);
    return data;
  },

  async remove(id: string): Promise<void> {
    await api.delete(`${basePath}/${id}`);
  },

  async portfolioMeta(): Promise<PortfolioMeta> {
    const { data } = await api.get(`${basePath}/meta`);
    return data;
  },

  async portfolioCharts(filters?: {
    status?: string;
    category?: string;
    search?: string;
  }): Promise<PortfolioCharts> {
    const params: Record<string, string> = {};
    if (filters?.status && filters.status !== 'all') params.status = filters.status;
    if (filters?.category && filters.category !== 'all') params.category = filters.category;
    if (filters?.search && filters.search.trim()) params.search = filters.search.trim();
    const { data } = await api.get(`${basePath}/portfolio`, { params });
    return data;
  },

  async priceHistory(id: string): Promise<PriceHistoryPoint[]> {
    const { data } = await api.get(`${basePath}/${id}/price-history`);
    return data;
  },

  async updatePrice(
    id: string,
    payload: { valor: number; preco?: number; timestamp?: string }
  ): Promise<Investment> {
    const { data } = await api.post(`${basePath}/${id}/price`, payload);
    return data;
  },

  async importPreview(source: ImportSource, file: File): Promise<ImportPreviewResult> {
    const form = new FormData();
    form.append('source', source);
    form.append('file', file);
    const { data } = await api.post(`${basePath}/import/preview`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (data && data.success === false) {
      throw Object.assign(new Error(data.error || 'Erro no preview'), { response: { data } });
    }
    return data;
  },

  async importCommit(payload: {
    source: ImportSource;
    filename: string;
    rows: ImportPreviewRow[];
    confirmUpsert: boolean;
  }): Promise<ImportCommitResult> {
    const { data } = await api.post(`${basePath}/import/commit`, payload);
    if (data && data.success === false) {
      throw Object.assign(new Error(data.error || 'Erro ao confirmar importação'), {
        response: { data },
      });
    }
    return data;
  },
};

export default InvestmentsAPI;
